import React from "react";
import { Stack, Typography, IconButton, Button, ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import etcStyles from "../../pages/css/etc.module.css"
import { DateTimePicker } from '../../components/DatePicker.js'
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';


/* 링크별 평균속도 오버레이 컴포넌트*/
export default function AvgSpeedByLink({ feature, onClose, data, onSearch, lightMode = false, initialConfig, onConfigChange }) {
    const yesterday = dayjs().subtract(1, 'day');
    const [viewType, setViewType] = React.useState(initialConfig.viewType);
    const [startDate, setStartDate] = React.useState(initialConfig.startDate);
    const [endDate, setEndDate] = React.useState(initialConfig.endDate);

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    // 뷰 타입 변경 핸들러
    const handleViewTypeChange = (event, newType) => {
        if (newType !== null && newType !== viewType) {
            setLoading(true);
            setViewType(newType);
        }
    };
    React.useEffect(() => {
        if (onConfigChange) {
            onConfigChange({ viewType, startDate, endDate });
        }
    }, [viewType, startDate, endDate]);

    // API 데이터 파싱 및 변환 함수
    const processApiData = React.useCallback((apiData) => {
        if (!apiData || !apiData.table || !apiData.table.rows) return [];
        const rawRows = apiData.table.rows;
        if (viewType === 'hourly') {
            return rawRows.map((row, index) => ({
                id: index,
                rawTime: row[1],
                speed: row[2] ? Number(row[2].toFixed(2)) : 0
            }));
        }
        const dailyGroups = rawRows.reduce((groups, row) => {
            const ymd = row[1].substring(0, 8);
            if (!groups[ymd]) {
                groups[ymd] = { speedSum: 0, count: 0 };
            }
            if (row[2] !== null) {
                groups[ymd].speedSum += row[2];
                groups[ymd].count += 1;
            }
            return groups;
        }, {});
        return Object.entries(dailyGroups).map(([ymd, stats], index) => ({
            id: index,
            rawTime: ymd,
            speed: stats.count > 0 ? Number((stats.speedSum / stats.count).toFixed(2)) : 0
        }));
    }, [viewType]);

    // 조회 핸들러
    const handleSearch = () => {
        if (!startDate || !endDate) return;
        
        if (startDate.isAfter(endDate)) {
            alert("시작일이 종료일보다 늦을 수 없습니다.");
            return;
        }

        if (endDate.isAfter(startDate.add(1, 'month'))) {
            alert("조회 기간은 최대 1달을 초과할 수 없습니다.");
            return;
        }
        const format = viewType === 'hourly' ? 'YYYYMMDDHH' : 'YYYYMMDD';
        const sdtm = startDate.format(format);
        const edtm = viewType === 'hourly' ? endDate.add(1, 'hour').format(format) : endDate.format(format);
        setLoading(true);
        
        if (onSearch) {
            onSearch(sdtm, edtm, viewType);
        }
    };
    
    React.useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature.LINK2_ID, viewType]);

    // CSV 다운로드 핸들러
    const handleDownload = () => {
        if (rows.length === 0) {
            alert("다운로드할 데이터가 없습니다.");
            return;
        }

        const headerRow = "시간,평균속도(km/h)";
        const dataRows = rows.map(row => {
            let timeStr = String(row.rawTime);
            const formattedTime = viewType === 'daily'
                ? dayjs(timeStr, 'YYYYMMDD').format('YYYY-MM-DD')
                : dayjs(timeStr, 'YYYYMMDDHH').format('YYYY-MM-DD HH시');
            return `${formattedTime},${row.speed} km/h`;
        });

        const csvString = [headerRow, ...dataRows].join("\n");
        const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        let dateInfo = viewType === 'hourly' ? `${startDate.format('YYYYMMDDHH')}-${endDate.format('YYYYMMDDHH')}` : `${startDate.format('YYYYMMDD')}-${endDate.format('YYYYMMDD')}`;
        const roadName = feature.name || "도로";
        
        link.setAttribute('download', `${roadName}_평균속도_${dateInfo}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // 데이터 변경 감지
    React.useEffect(() => {
        if (!data || !data.table || !data.table.rows) {
            return;
        }
        try {
            const formattedRows = processApiData(data);
            setRows(formattedRows);
        } catch (e) {
            console.error("데이터 처리 중 오류:", e);
            setRows([]);
        } finally {
            setLoading(false);
        }

    }, [data, processApiData]);

    // DataGrid 컬럼 정의
    const columns = React.useMemo(() => [
        { 
            field: 'rawTime',
            headerName: '시간', 
            flex: 1, 
            align: 'center', 
            headerAlign: 'center',
            valueFormatter: (params) => {
                const val = String(params.value);
                if (!val) return '';
                return viewType === 'daily' 
                    ? dayjs(val, 'YYYYMMDD').format('M월 D일')
                    : dayjs(val, 'YYYYMMDDHH').format('M월 D일 H시');
            }
        },
        { 
            field: 'speed', 
            headerName: '평균속도', 
            flex: 1, 
            align: 'center', 
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography variant="body2" sx={{ 
                    fontWeight: 'bold', 
                    color: lightMode ? '#333' : '#64b5f6' 
                }}>
                    {params.value} km/h
                </Typography>
            )
        },
    ], [viewType]);

    // 라이트 모드 스타일 정의
    const lightModeStyles = lightMode ? {
        pnlWrap: {
            backgroundColor: 'rgba(255, 255, 255, 0.95) !important',
            color: '#333 !important',
            border: '1px solid rgba(0, 0, 0, 0.1)',
        },
        pnlHeader: {
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        },
        pnlTit: {
            color: '#333 !important',
        },
        toggleButton: {
            bgcolor: lightMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255,255,255,0.1)',
            '& .MuiToggleButton-root': { 
                color: lightMode ? '#666' : '#ccc', 
                border: 'none', 
                py: 0.5, 
                fontSize: '12px',
                '&:hover': {
                    backgroundColor: lightMode ? 'rgba(83, 100, 255, 0.08)' : 'rgba(255,255,255,0.15)',
                }
            },
            '& .Mui-selected': { 
                bgcolor: '#5364FF', 
                color: 'white', 
                fontSize: '12px',
                '&:hover': {
                    backgroundColor: '#919cff',
                }
            }
        },
        closeButton: {
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            color: '#333',
            '&:hover': { bgcolor: '#f5f5f5', color: '#000' },
        },
        dataGrid: {
            '& .MuiDataGrid-main': { color: '#333' },
            '& .MuiDataGrid-columnHeaders': { 
                bgcolor: '#f5f5f5', 
                color: '#333', 
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)' 
            },
            '& .MuiDataGrid-cell': { 
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                '&:focus': { outline: 'none' },
                '&:focus-within': { outline: 'none' }
            },
            '& .MuiDataGrid-row:hover': {
                backgroundColor: 'rgb(83 100 255 / 12%)',
            },
            '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: 'rgb(83 100 255 / 20%) !important',
                color: '#333 !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: 'rgb(83 100 255 / 30%) !important',
            },
            '& .MuiDataGrid-virtualScroller': {
                '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                '&::-webkit-scrollbar-thumb': { 
                    bgcolor: 'rgba(0, 0, 0, 0.2)', 
                    borderRadius: '3px' 
                }
            }
        },
        loadingText: {
            color: '#666'
        }
    } : {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <div
                className={etcStyles.pnlWrap}
                style={{ 
                    position: 'relative', 
                    overflow: 'visible',
                    ...(lightMode ? lightModeStyles.pnlWrap : {})
                }}
                elevation={0}>
                {/* 닫기 버튼 측면에 배치 */}
                <IconButton 
                    onClick={onClose}
                    sx={{ 
                        position: 'absolute',
                        left: '-30px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '30px',
                        height: '60px',
                        borderRadius: '8px 0 0 8px',
                        zIndex: 1000,
                        ...(lightMode ? lightModeStyles.closeButton : {
                            bgcolor: '$rgba(50, 50, 50, 0.9)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'white',
                            '&:hover': { bgcolor: '#444' },
                        })
                    }}
                >
                    <PlayArrowIcon 
                        sx={{ 
                            fontSize: '20px' 
                        }} 
                    />
                </IconButton>
                {/* 헤더 */}
                <Stack className={etcStyles.pnlHeader} direction="row" alignItems="center" justifyContent="space-between" sx={lightMode ? lightModeStyles.pnlHeader : {}}>
                    <Typography className={etcStyles.pnlTit} variant="h6" fontWeight="bold" sx={lightMode ? lightModeStyles.pnlTit : {}}>
                        {feature.roadNm || feature.name || "도로 정보 없음"}
                    </Typography>
                    <ToggleButtonGroup
                        className={etcStyles.dayTglBtn}
                        value={viewType}
                        exclusive
                        onChange={handleViewTypeChange}
                        size="small"
                        sx={lightMode ? {
                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                            '& .MuiToggleButton-root': { 
                                color: '#666', 
                                border: 'none', 
                                py: 0.5, 
                                fontSize: '12px',
                                '&:hover': {
                                    backgroundColor: 'rgba(83, 100, 255, 0.08)',
                                }
                            },
                            '& .Mui-selected': { 
                                bgcolor: '#5364FF !important', 
                                color: 'white !important', 
                                fontSize: '12px',
                                '&:hover': {
                                    backgroundColor: '#919cff !important',
                                }
                            }
                        } : { 
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '& .MuiToggleButton-root': { color: '#ccc', border: 'none', py: 0.5, fontSize:'12px' },
                            '& .Mui-selected': { bgcolor: '#5364FF', color: 'white', fontSize:'12px' }
                        }}
                    >
                        <ToggleButton value="hourly">시간별</ToggleButton>
                        <ToggleButton value="daily">일별</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                {/* 컨트롤러 영역 */}
                <Stack className={etcStyles.pnlSrch}>
                    <div className="flx a_center gap10">
                        {/* 상단: 뷰 타입 및 조회 버튼 */}
                        <div className={etcStyles.inputWrap} direction="row" alignItems="center" sx={{ minHeight: '32px' }}>
                            <DateTimePicker
                                mode={viewType === 'hourly' ? 60 : 24}
                                label="조회시작일"
                                value={startDate}
                                maxDate={yesterday.endOf('day')}
                                onChange={setStartDate}
                                lightMode={lightMode}
                                inputSx={{
                                    '& .MuiOutlinedInput-root input': {
                                    fontSize: '12px',
                                    padding: '6px 8px',
                                    color: lightMode ? '#333' : 'white',
                                    },
                                    /* 라벨 기본 상태 */
                                    '& .MuiInputLabel-root': {
                                    fontSize: '16px',
                                    transform: 'translate(14px, 14px) scale(1)',
                                    color: lightMode ? '#666' : 'white',
                                    },

                                    /* 포커스 or 값 있을 때 (위로 올라간 상태) */
                                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                    fontSize: '10px',
                                    transform: 'translate(14px, -6px) scale(0.9)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                    color: lightMode ? '#1976d2' : 'white',
                                    },
                                    '& .MuiSvgIcon-root': {
                                    fontSize: '18px',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: lightMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#5364FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#6C7AFC',
                                    },
                                    },
                                }}
                            />
                            <DateTimePicker
                                mode={viewType === 'hourly' ? 60 : 24} 
                                label="조회종료일"
                                value={endDate}
                                maxDate={yesterday.endOf('day')}
                                onChange={setEndDate}
                                lightMode={lightMode}
                                inputSx={{
                                    '& .MuiOutlinedInput-root input': {
                                    fontSize: '12px',
                                    padding: '6px 8px',
                                    color: lightMode ? '#333' : 'white',
                                    },
                                    /* 라벨 기본 상태 */
                                    '& .MuiInputLabel-root': {
                                    fontSize: '16px',
                                    transform: 'translate(14px, 14px) scale(1)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    /* 포커스 or 값 있을 때 (위로 올라간 상태) */
                                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                                    fontSize: '10px',
                                    transform: 'translate(14px, -6px) scale(0.9)',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                    color: lightMode ? '#1976d2' : 'white',
                                    },
                                    '& .MuiSvgIcon-root': {
                                    fontSize: '18px',
                                    color: lightMode ? '#666' : 'white',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: lightMode ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.5)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#5364FF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: lightMode ? '#1976d2' : '#6C7AFC',
                                    },
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div className={etcStyles.btnWrap}>
                            <Button variant="contained" size="small" onClick={handleSearch} sx={{ bgcolor: '#5364FF', color:'#fff', px: 0 }}>
                                <SearchIcon />
                            </Button>
                            <Button variant="outlined" size="small" onClick={handleDownload} sx={{ 
                                color: lightMode ? '#333' : '#ccc', 
                                borderColor: lightMode ? '#333' : '#ccc', 
                                px: 0,
                                '&:hover': {
                                    borderColor: lightMode ? '#000' : '#fff',
                                    color: lightMode ? '#000' : '#fff',
                                }
                            }}>
                                <FileDownloadIcon />
                            </Button>
                    </div>
            </Stack>

                {/* 데이터 그리드 (리스트 형태) */}
                <div className={etcStyles.gridWrap}>
                    <DataGrid
                        key={`grid-${viewType}-${feature.LINK2_ID}`}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        density="compact"
                        rowHeight={42}
                        columnHeaderHeight={42}
                        hideFooterSelectedRowCount
                        sx={{
                            fontSize:'12px',
                            border: 'none',
                            borderRadius:0,
                            '& .MuiDataGrid-footerContainer': {
                                '& *' : {  color: '#333' }
                            },
                            '& .MuiTablePagination-root': {
                                height: '36px',
                                minHeight: '36px',
                                overflow: 'hidden',
                            },
                            '& .MuiTablePagination-toolbar': {
                                minHeight: '36px',
                                height: '36px',
                                paddingY: '0',
                                paddingRight: '2px',
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                margin: 0,
                                fontSize: '14px',
                            },
                            '& .MuiTablePagination-actions': {
                                '& button': {
                                    padding: '2px',
                                }
                            },
                            '& .MuiInputBase-root': {
                                marginRight: '8px',
                                fontSize: '14px',
                            },
                            '& .MuiDataGrid-overlay': { backgroundColor: 'transparent' },
                            ...(lightMode ? lightModeStyles.dataGrid : {
                                '& .MuiDataGrid-main': { color: 'white' },
                                '& .MuiDataGrid-columnHeaders': { bgcolor: '#333', color: 'white', fontSize: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
                                '& .MuiDataGrid-columnHeaderTitleContainer': { justifyContent: 'center', fontSize: '12px', },
                                '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(255,255,255,0.05)' , fontSize: '12px',},
                                '& .text-blue': { color: '#64b5f6 !important', fontSize: '12px', },
                                '& .text-red': { color: '#ef5350 !important', fontSize: '12px', },
                                '& .MuiDataGrid-virtualScroller': {
                                    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '3px' }
                                }
                            })
                        }}
                    />
                </div>
            </div>
        </LocalizationProvider>
    );
}
